import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BusinessUserProfile, BusinessCompany, BusinessPermissions } from '@/types/business';
import { useVehicleStore } from '@/stores/vehicle';

interface BusinessAuthStore {
  user: BusinessUserProfile | null;
  company: BusinessCompany | null;
  token: string | null;

  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  isMechanic: () => boolean;
  hasPermission: (perm: keyof BusinessPermissions) => boolean;
  setAuth: (user: BusinessUserProfile, company: BusinessCompany, token: string) => void;
  setUser: (user: BusinessUserProfile) => void;
  setCompany: (company: BusinessCompany) => void;
  logout: () => void;
}

/** JWT:ns exp (sekunder) har passerat. Kan tokenen inte läsas räknas den som giltig — servern avgör. */
function tokenUtgangen(token: string | null): boolean {
  if (!token) return true;
  try {
    const del = token.split('.')[1];
    if (!del) return false;
    const { exp } = JSON.parse(atob(del.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof exp === 'number' && exp * 1000 < Date.now();
  } catch { return false; }
}

export const useBusinessAuthStore = create<BusinessAuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      company: null,
      token: null,

      // En utgången token räknas som utloggad. Förut räckte det att den fanns:
      // en företagskund vars session gått ut skickades från vanliga kassan till
      // företagskassan, fick 401 och hamnade på inloggningen (2026-09-26).
      isAuthenticated: () => !!get().token && !!get().user && !tokenUtgangen(get().token),

      isAdmin: () => get().user?.role === 'admin' || get().user?.role === 'platschef',

      isMechanic: () => get().user?.is_mechanic === true || get().user?.role === 'mechanic',

      hasPermission: (perm) => {
        const user = get().user;
        if (!user) return false;
        if (user.permissions) return user.permissions[perm] === true;
        // Fallback for old tokens without permissions
        return user.role === 'admin' || user.role === 'platschef';
      },

      setAuth: (user, company, token) => {
        set({ user, company, token });
      },

      setUser: (user) => {
        set({ user });
      },

      setCompany: (company) => {
        set({ company });
      },

      logout: () => {
        set({ user: null, company: null, token: null });
        // Clear B2B vehicle data to prevent leaking to public frontend
        try {
          useVehicleStore.getState().clearVehicle();
          useVehicleStore.setState({ history: [] });
          localStorage.removeItem('autofrance-current-vehicle');
        } catch { /* ignore */ }
        // Clear chat session IDs to prevent data leaking to next user
        try {
          const keys = Object.keys(sessionStorage).filter(k => k.startsWith('autofrance-chat-session-'));
          keys.forEach(k => sessionStorage.removeItem(k));
        } catch { /* ignore */ }
        // Clear login count so next user gets fresh greeting
        try { localStorage.removeItem('autofrance-login-count'); } catch { /* ignore */ }
      },
    }),
    {
      name: 'autofrance-business-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        company: state.company,
      }),
    }
  )
);
